import React, { Component } from "react";
import PropTypes from "prop-types";
import throttle from "lodash/throttle";
import style from "./style.module.css";
import Fields from "./Fields";
import Pointer from "./Pointer";
import SaturationPointer from "./SaturationPointer";
import SliderSwatches from "./SliderSwatches";
import Button from "../button";

import { ColorWrap, Saturation, Hue } from "react-color/lib/components/common";

export class ColorPicker extends Component {
  static defaultProps = {
    onClose: () => {},
    width: 300
  };

  constructor(props) {
    super(props);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.hueContainerRef = React.createRef();
    this.hueTouchOverlayRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);

    // On iOS Safari, react-color's Hue component fires unthrottled onChange
    // on every touchmove event. The rapid re-renders cause iOS to flicker the
    // input accessory bar on/off. Saturation doesn't have this issue because
    // it throttles onChange at 50ms.
    //
    // Fix: place a touch overlay on the hue area that intercepts all touch
    // events natively (before React's delegation), calculates hue changes
    // with throttling, and stops the events from reaching react-color.
    const overlay = this.hueTouchOverlayRef.current;
    if (overlay) {
      overlay.addEventListener("touchstart", this.handleHueTouchStart, {
        passive: false
      });
      overlay.addEventListener("touchmove", this.handleHueTouchMove, {
        passive: false
      });
      overlay.addEventListener("touchend", this.handleHueTouchEnd, {
        passive: false
      });
      overlay.addEventListener("touchcancel", this.handleHueTouchEnd, {
        passive: false
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    this.throttledHueChange.cancel?.();
    const overlay = this.hueTouchOverlayRef.current;
    if (overlay) {
      overlay.removeEventListener("touchstart", this.handleHueTouchStart);
      overlay.removeEventListener("touchmove", this.handleHueTouchMove);
      overlay.removeEventListener("touchend", this.handleHueTouchEnd);
      overlay.removeEventListener("touchcancel", this.handleHueTouchEnd);
    }
  }

  calculateHueFromTouch(touch) {
    const container = this.hueContainerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const containerHeight = rect.height;

    let h;
    if (y < 0) {
      h = 359;
    } else if (y > containerHeight) {
      h = 0;
    } else {
      const percent = -((y * 100) / containerHeight) + 100;
      h = (360 * percent) / 100;
    }

    return {
      h,
      s: this.props.hsl.s,
      l: this.props.hsl.l,
      a: this.props.hsl.a,
      source: "hsl"
    };
  }

  throttledHueChange = throttle(change => {
    if (change && typeof this.props.onChange === "function") {
      this.props.onChange(change);
    }
  }, 50);

  handleHueTouchStart = e => {
    e.preventDefault();
    e.stopPropagation();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const touch = e.touches[0];
    if (touch) {
      const change = this.calculateHueFromTouch(touch);
      this.throttledHueChange(change);
    }
  };

  handleHueTouchMove = e => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    if (touch) {
      const change = this.calculateHueFromTouch(touch);
      this.throttledHueChange(change);
    }
  };

  handleHueTouchEnd = e => {
    e.preventDefault();
    e.stopPropagation();
    this.throttledHueChange.flush?.();
  };

  handleClickOutside(event) {
    const { element } = this;
    const isClickOutside =
      element &&
      !element.contains(event.target) &&
      !element.parentElement.parentElement.contains(event.target) &&
      // ignore targets that are not in the dom at the time of trigger
      event.target.isConnected;
    if (isClickOutside) {
      this.props.onClose();
    }
  }

  render() {
    const {
      rgb,
      hex,
      hsv,
      hsl,
      onChange,
      onSave,
      isSaveButtonHidden = false,
      className = "",
      isDisplayHeader = false,
      isRichText
    } = this.props;

    const styles = {
      saturation: {
        radius: "3px",
        shadow: "inset 0 0 0 1px rgba(0,0,0,.15), inset 0 0 4px rgba(0,0,0,.25)"
      },
      hue: {
        radius: "2px",
        shadow: "inset 0 0 0 1px rgba(0,0,0,.15), inset 0 0 4px rgba(0,0,0,.25)"
      }
    };

    return (
      <div
        className={`easil-picker ${style.wrapper} ${className}`}
        ref={element => (this.element = element)}
      >
        {isDisplayHeader && (
          <div className={style.pickerHeader}>
            <div>Select a color</div>
            <Button theme="blueSolidNoBackgroundNoBorder" onClick={onSave}>
              Close
            </Button>
          </div>
        )}
        <Fields
          rgb={rgb}
          hsl={hsl}
          hex={hex}
          onChange={onChange}
          disableAlpha={true}
          isRichText={isRichText}
        />
        <div className={style.colorsWrapper}>
          <SliderSwatches
            className={style.swatchesWrapper}
            hsl={hsl}
            onClick={onChange}
          />
          <div className={style.gradientWrapper}>
            <Saturation
              style={styles.saturation}
              pointer={SaturationPointer}
              hsl={hsl}
              hsv={hsv}
              onChange={onChange}
            />
          </div>
          <div className={style.hue} ref={this.hueContainerRef}>
            <Hue
              direction={"vertical"}
              style={styles.hue}
              hsl={hsl}
              pointer={Pointer}
              onChange={onChange}
            />
            <div
              ref={this.hueTouchOverlayRef}
              className={style.hueTouchOverlay}
            />
          </div>
        </div>
        {!isSaveButtonHidden && (
          <Button onClick={onSave} theme="blueSolid" isExtraWide>
            Save
          </Button>
        )}
      </div>
    );
  }
}

ColorPicker.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ColorWrap(ColorPicker);
